import { faArrowsRotate, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import { LeadTypeCategories } from "data/commonData";
import axiosInstance from "helpers/api";
import { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";

interface LeadsFilterModalProps {
  show: boolean;
  handleClose: () => void;
  setLeads: any;
}

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  source: string;
};

const LeadsFilterModal = ({
  show,
  handleClose,
  setLeads,
}: LeadsFilterModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  const [leadType, setLeadType] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState([]);

  const fetchLeadTypes = async () => {
    try {
      const response = await axiosInstance.get("/leads/types");
      setLeadTypes(response.data.data.leadTypes);
    } catch (error: any) {      
      toast.error(error?.response?.data?.message);
    }
  };

  const matchStatus = async (id: any) => {
    setLeadType(id);

    try {
      const response = await axiosInstance.get(`/leads/status/${id}`);
      setSelectedLeadStatus(response.data.data.leadTypes);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  const handleSelectStatus = (e: any) => {
    setLeadStatus(e);
  };

  const handleReset = async () => {
    try {
      // Reset form and state
      setLeadType(null);
      setLeadStatus(null);
      setSelectedLeadStatus([]);
      reset();
      
      // Fetch all leads without filters
      const response = await axiosInstance.get("/dashboard/leads");
      // Backend now returns { success: true, data: { leads: [...], pagination: {...} } }
      const leadsData = response.data.data?.leads || response.data.leads || response.data.data || response.data || [];
      
      // Apply the same data transformation as in LeadsAI.tsx
      const transformedLeads = leadsData.map((lead: any) => {        
        // Parse JSON tags to handle multiple tags properly
        let parsedTags = [];
        if (lead.lead_tags) {
          try {
            if (typeof lead.lead_tags === 'string') {
              try {
                parsedTags = JSON.parse(lead.lead_tags);
              } catch {
                parsedTags = [lead.lead_tags];
              }
            } else if (Array.isArray(lead.lead_tags)) {
              parsedTags = lead.lead_tags;
            } else {
              parsedTags = [String(lead.lead_tags)];
            }
            
            if (!Array.isArray(parsedTags)) {
              parsedTags = [String(parsedTags)];
            }
            parsedTags = parsedTags.filter(tag => tag && typeof tag === 'string' && tag.trim()).slice(0, 3);
            
          } catch (e) {
            console.log('Error parsing tags:', e);
            parsedTags = [];
          }
        }

        return {
          ...lead,
          lead_last_name: lead.lead_last_name || '',
          lead_phone: lead.lead_phone || '',
          lead_email: lead.lead_email || '',
          lead_source: lead.lead_source || 'Unknown',
          lead_types_id: lead.lead_types_id || null,
          lead_type: lead.lead_type || null,
          lead_status: lead.lead_status || null,
          lead_tags: parsedTags,
          lead_score: lead.lead_score || 50.0,
          priority_level: lead.priority_level || 'medium'
        };
      });
      
      setLeads(transformedLeads);
      handleClose();
    } catch (error: any) {
      console.error('❌ [FILTER RESET] Error:', error);
      toast.error('Failed to reset filters');
    }
  };

  useEffect(() => {
    fetchLeadTypes();
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      console.log('🔍 [FILTER MODAL] Applying filters:', { leadType, leadStatus });
      const response = await axiosInstance.get("/dashboard/leads", {
        params: {
          lead_types_id: leadType,
          lead_type_status_id: leadStatus,
        },
      });
      console.log('🔍 [FILTER MODAL] Filter response:', response.data);

      // Handle response structure - match CRM dashboard logic
      // Backend now returns { success: true, data: { leads: [...], pagination: {...} } }
      const leadsData = response.data.data?.leads || response.data.leads || response.data.data || response.data || [];
      console.log('🔍 [FILTER MODAL] Filtered leads:', leadsData.length);
      
      // Apply the same data transformation as in LeadsAI.tsx
      const transformedLeads = leadsData.map((lead: any) => {        
        // Parse JSON tags to handle multiple tags properly
        let parsedTags = [];
        if (lead.lead_tags) {
          try {
            if (typeof lead.lead_tags === 'string') {
              // Try to parse as JSON first
              try {
                parsedTags = JSON.parse(lead.lead_tags);
              } catch {
                // If not JSON, treat as single tag
                parsedTags = [lead.lead_tags];
              }
            } else if (Array.isArray(lead.lead_tags)) {
              parsedTags = lead.lead_tags;
            } else {
              parsedTags = [String(lead.lead_tags)];
            }
            
            // Ensure it's always an array and filter out empty values
            if (!Array.isArray(parsedTags)) {
              parsedTags = [String(parsedTags)];
            }
            // Filter out invalid tags and limit to 3 tags max
            parsedTags = parsedTags.filter(tag => tag && typeof tag === 'string' && tag.trim()).slice(0, 3);
            
          } catch (e) {
            console.log('Error parsing tags:', e);
            parsedTags = [];
          }
        }

        return {
          ...lead,
          lead_last_name: lead.lead_last_name || '',
          lead_phone: lead.lead_phone || '',
          lead_email: lead.lead_email || '',
          lead_source: lead.lead_source || 'Unknown',
          lead_types_id: lead.lead_types_id || null,
          lead_type: lead.lead_type || null, // Keep full object from LeadTypes join
          lead_status: lead.lead_status || null, // Keep full object from LeadStatus join
          lead_tags: parsedTags,
          lead_score: lead.lead_score || 50.0,
          priority_level: lead.priority_level || 'medium'
        };
      });
      
      setLeads(transformedLeads);
      handleClose();
    } catch (error: any) {
      console.error('❌ [FILTER MODAL] Error:', error);
      toast.error(error?.response?.data?.message || 'Failed to apply filters');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="p-0" centered>
      <Modal.Header className="border-translucent px-4">
        <h5 className="modal-title text-body-highlight fs-6 lh-sm">Filter</h5>
        <Button className="p-1 ms-auto" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-9" />
        </Button>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="pt-4 pb-2 px-4">
          <div className="mb-3">
            <Form.Group>
              <Form.Label>Lead Type</Form.Label>
              <Form.Select 
                value={leadType || ""} 
                onChange={(e) => matchStatus(e.target.value)}
              >
                <option value="" disabled>
                  Select
                </option>
                {leadTypes.map((item: any, i: any) => (
                  <option key={i} value={item.id}>
                    {item.lead_type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="mb-3">
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                className="form-select"
                value={leadStatus || ""}
                onChange={(e) => handleSelectStatus(e.target.value)}
              >
                <option value="" disabled>
                  Select
                </option>
                {selectedLeadStatus.map((item: any, i: any) => (
                  <option key={i} value={item.id}>
                    {item.status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
          <Button
            variant="phoenix-primary"
            size="sm"
            className="px-4 fs-10 my-0"
            startIcon={
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2 fs-10" />
            }
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            className="px-9 fs-9 my-0"
          >
            Done
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LeadsFilterModal;
